import Enterprise16 from '@carbon/icons-react/es/enterprise/16';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MemberType } from 'api/models';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import {
  PopoverMultiSelect,
  TriggerProps,
} from 'components/atoms/select/popover';
import { MultiValue, Option } from 'components/atoms/select/types';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import styles from './responsible-dropdown.module.css';

const MultiSelectTrigger = ({ value, openPopover, size }: TriggerProps) => {
  const hasValues = value && value.length > 0;

  return (
    <Button
      type="link"
      icon={!hasValues && <Enterprise16 />}
      size={size}
      onClick={openPopover}
    >
      {hasValues ? (
        <AvatarGroup maxCount={2}>
          {value!.map((opt) => (
            <Avatar
              key={opt.value}
              name={opt.label}
              src={opt.imageUrl}
              size="small"
            />
          ))}
        </AvatarGroup>
      ) : (
        <FormattedMessage id="choose" />
      )}
    </Button>
  );
};

export const ResponsibleDropdown: FC<{
  value?: MemberType[];
}> = ({ value }) => {
  const [selected, setSelected] = useState<MultiValue>();
  const user = useUser();
  const intl = useIntl();
  const usersTeamText = intl.formatMessage(
    {
      id: 'templates.responsible_dropdown.company_team',
    },
    {
      companyName: user.company.companyName,
    },
  );
  const customersTeamText = useTranslation(
    'templates.responsible_dropdown.customer_team',
  );

  const options: Option[] = useMemo(() => {
    return [
      {
        value: MemberType.Native,
        label: usersTeamText,
        imageUrl: user.company.companyLogoUrl,
      },
      {
        value: MemberType.External,
        label: customersTeamText,
      },
    ];
  }, [customersTeamText, usersTeamText, user.company.companyLogoUrl]);

  useEffect(() => {
    setSelected(
      value
        ?.map((v) => options.find((option) => option.value === v)!)
        .filter(Boolean),
    );
  }, [options, value]);

  return (
    <PopoverMultiSelect
      options={options}
      overlayClassName={styles.overlay}
      renderTrigger={MultiSelectTrigger}
      value={selected}
    />
  );
};
